import { invalidateDashboardCache } from '../hooks/useHomeDashboard';
import { invalidateSavedItemsCache } from '../hooks/useSavedItemsData';
import { invalidateFeedCache } from '../hooks/feedCache';
import { invalidateDetailCache, invalidateDetailCacheType } from '../hooks/detailCache';
import { clearDestinationViews } from '../hooks/destinationViewCache';
import { invalidateCalendarCache } from '../hooks/calendarCache';
import { invalidatePlannerCache } from '../hooks/plannerCache';
import { clearTripCaches } from '../hooks/useTripState';
import { clearTripCostsCache } from '../hooks/tripCostsCache';
import { invalidateChildActivityHistoryCache } from '../hooks/childActivityHistoryCache';
import { invalidateBookingStatusCache } from '../hooks/bookingStatusCache';
import { invalidatePlacePhotos } from '../hooks/usePlacePhotos';
import { clearNewsletterDraft } from '../hooks/newsletterDraft';
import { clearHomeLocation } from '../hooks/useHomeLocation';
import { recordBlockedUser, clearBlockedUsers } from './blockedCards';
import AuthService from '../services/authService';
import { clearUnsavedMarks } from './placeEnrichmentMarks';

// Clear the MySavedItems summary cache, the Home dashboard cache, and the
// Discover feed cache. Called from any mutation that could change what the user
// sees on any of those surfaces (save/unsave, create/delete collection or album,
// etc.) when the mutation happens OUTSIDE the useSavedItemsData hook, which
// handles its own invalidation internally. No userId arg: single-session memory,
// so clearing all entries is equivalent to clearing the current user's entry.
export const invalidateUserContentCaches = () => {
  invalidateSavedItemsCache();
  invalidateDashboardCache();
  invalidateFeedCache();
  invalidateLikeStateCaches();
};

// The detail pages whose cached payload carries like/save state: a place page,
// and a destination page's sections (every card on it). Both restore a fresh
// hit WITHOUT asking the server, and the server counts and toggles a place's
// like across every id it is known under — so a stale "unliked" copy turns the
// next tap into an UNLIKE of the heart the viewer just made elsewhere, while
// the card says "liked". Nothing tells them. Every like handler calls this;
// it is exported apart from invalidateUserContentCaches so the feed's like
// handler, which has no business clearing the saved-items cache, can call
// just this.
export const invalidateLikeStateCaches = () => {
  invalidateDestinationCaches();
  invalidatePlaceDetailCaches();
};

export const invalidateDestinationCaches = () => {
  invalidateDetailCacheType('destination');
};

export const invalidatePlaceDetailCaches = () => {
  invalidateDetailCacheType('place');
};

// Clear EVERY session-scoped module cache, including the detail-page cache.
// Called on auth boundaries (logout, token expiry) so nothing cached for one
// account — like/save/follow flags, friend-gated content — can render for the
// next account on the same device. The planner/calendar/trip/child caches are
// user-keyed so they can't cross accounts, but they're cleared here anyway to
// keep this function's contract simple: after an auth boundary, no module
// cache holds anything.
export const invalidateSessionCaches = () => {
  // User-typed PII, not fetched data — but the same contract: the
  // anonymous newsletter draft must not survive a logout on a shared
  // device (self-review round 5).
  clearNewsletterDraft();
  // Whom this account blocked is this account's; the next one starts clean.
  clearBlockedUsers();
  invalidateUserContentCaches();
  invalidateDetailCache();
  clearDestinationViews();
  invalidateCalendarCache();
  invalidatePlannerCache();
  clearTripCaches();
  // A trip's ledger (expenses, splits, member names) as one account read it.
  clearTripCostsCache();
  invalidateChildActivityHistoryCache();
  invalidateBookingStatusCache();
  // Viewer-dependent since the community-photos owner arm (2026-09-03):
  // holds the previous user's own private photos otherwise.
  invalidatePlacePhotos();
  // The home is the account's own (and may be hidden from everyone else);
  // the next account's /welcome prefilled it and saved it as theirs
  // (BH-10-01). Mounted instances are told there is none.
  clearHomeLocation();
  clearSearchSessionState();
  // An admin's unsaved Place Enrichment marks: the next admin on this tab
  // must not inherit (and save) them.
  clearUnsavedMarks();
  // The /me cache, and any /me still in flight for the ended session
  // (BH-02-02). The token-expiry path reaches here without AuthService.signOut.
  AuthService.clearUserCache();
};

// Search's per-tab state in sessionStorage, written by "Fill your day" on a
// trip (ItineraryTab) and by Home's search (useHomeSearch): the trip's name,
// days and destinations, and the travellers picked in Who's going — names,
// children included. Search reads it back with no account check, so it goes
// at an auth boundary like the module caches (BH-08-01).
const SEARCH_SESSION_KEY = 'tripSearchContext';
const SEARCH_FILTERS_PREFIX = 'searchFilters_';
export const clearSearchSessionState = () => {
  try {
    const doomed = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key === SEARCH_SESSION_KEY || (key && key.startsWith(SEARCH_FILTERS_PREFIX))) doomed.push(key);
    }
    doomed.forEach((key) => sessionStorage.removeItem(key));
  } catch (e) { /* storage blocked: nothing was stored either */ }
};

// Fired when the user answers an invitation — from Home's Coming up section,
// the notification center, or anywhere else. Clears the caches an answer
// changes (Home shows the invite / the newly accepted plan; the planner lists
// accepted activities) and tells a mounted Home to refetch: the notification
// center is an overlay, so Home can be on screen underneath it and would
// otherwise keep showing the invite it just answered.
export const INVITES_CHANGED_EVENT = 'invitesChanged';
export const notifyInvitesChanged = () => {
  invalidateDashboardCache();
  invalidatePlannerCache();
  // A trip loaded before the answer was loaded as a non-member (C114).
  clearTripCaches();
  window.dispatchEvent(new CustomEvent(INVITES_CHANGED_EVENT));
};

// Fired after a block succeeds, wherever it was made — a feed card, a report,
// the comments modal, a profile (C74, Arc N). The server stops sending the
// blocked person's content at once (blocking busts its feed caches), but the
// client held it in three places: the module caches, which would serve it
// again on the next visit; the detail cache, which restores a page on back
// navigation without asking; and the cards on screen. This clears the first
// two and tells every mounted list to drop the third (useDropBlockedUser). A
// request already in flight was answered before the block, so the person is
// also recorded for the loaders to filter what arrives (withoutBlockedUsers).
export const USER_BLOCKED_EVENT = 'userBlocked';
export const notifyUserBlocked = (userId) => {
  recordBlockedUser(userId);
  invalidateUserContentCaches();
  invalidateDetailCache();
  window.dispatchEvent(new CustomEvent(USER_BLOCKED_EVENT, { detail: { userId } }));
};
